/**
 * Intended to be implemented by other components to help create a modal-based step-by-step
 * communication workflow. This component provides the means to allow a user to select
 * which contacts they would like to send communication to, then depends on the caller
 * to provide subsequent components.
 */

import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { Control, Form } from 'react-redux-form';
import classNames from 'classnames';

import SelectContacts from '../forms/SelectContacts';

import './styles/snippet_editor.scss';
import ValidationErrors from '../forms/ValidationErrors';

import InfoTooltip from '../shared/InfoTooltip';
import './styles/CommunicationWorkflow.scss';

const WizardSelectContacts = ({ customer, communicationType, selectableContactTypes }) => {
  const mapLabels = (value, label) => value === 'selected' ?
    'Selected contacts' :
    `${label} of selected contacts`;

  return <div>
    <div className='communication-workflow-modal-contact-select-header'>Who would you like to receive the message?</div>
    <SelectContacts model={`.relation[]`} mapLabels={mapLabels} required={false} customer={customer} selectableContactTypes={selectableContactTypes} />

    <div className='communication-workflow-modal-additional-options'>
      <div className='communication-workflow-modal-additional-options-header'>Additional Options</div>

      <label className="checkbox">
        <Control.checkbox
          model={`.combine_family_contacts`}
          value={true}
        />
        Send as family-based {communicationType}
        <InfoTooltip preNbsp>
          When selected, only one {communicationType} will be sent to each contact regardless of the
          number of students for which they are responsible
        </InfoTooltip>
      </label>
    </div>
  </div>;
};

class CommunicationWorkflow extends React.Component {
  static propTypes = {
    actions: PropTypes.object.isRequired,
    baseModel: PropTypes.string.isRequired,
    confirmation: PropTypes.element,
    components: PropTypes.array,
    form: PropTypes.object.isRequired,
    hideContactSelect: PropTypes.bool,
    header: PropTypes.string.isRequired,
    ids: PropTypes.array,
    page: PropTypes.number.isRequired,
    model: PropTypes.object.isRequired,

    getRelatedContacts: PropTypes.func,
    reset: PropTypes.func,
    recipientFilter: PropTypes.func,
    setPage: PropTypes.func,
    setRecipients: PropTypes.func,
    communicationType: PropTypes.string,
  };

  static defaultProps = {
    actions: {},
    baseModel: '',
    components: [],
    hideContactSelect: false,
    header: '',
    ids: [],
    form: {},
    page: 0,
    model: {},

    getRelatedContacts: () => false,
    recipientFilter: recipients => recipients,
    reset: () => false,
    setPage: () => false,
    setRecipients: () => false,

    communicationType: 'email',
  };

  constructor(props) {
    super(props);

    this.state = {
      showModal: true
    }
  }

  render() {
    const {
      baseModel,
      header,
      page
    } = this.props;

    const components = this.components() || [];
    const SelectedComponent = components[page];
    const modelName = baseModel + SelectedComponent.props.formPrefix;
    const fieldNames = this.fieldNames(SelectedComponent.props.fields);
    const { showModal } = this.state;

    /*
     * We have to wrap the Modal in a div so that close can unmount the component.
     * This is an anti-pattern; we should figure out a better way of doing this.
     */
    return showModal && (
      <div>
        <Modal
          show
          backdrop='static'
          keyboard={false}
          animation={false}
          className={classNames('communication-workflow-modal', SelectedComponent.props.className)}
          onHide={() => this.close()}
        >
          <Form
            model={modelName}
            onSubmit={
              (values) => SelectedComponent.props.onSubmit(values)
                .then(() => this.nextComponent())
            }
            onChange={SelectedComponent.props.onChange}
          >
            <Modal.Header>
              <h1 className='modal-title'>{header}</h1>
              <button type='button' className='btn-close' aria-label='Close' onClick={() => this.close()} />
            </Modal.Header>

            <Modal.Body>
              <ValidationErrors
                errors={this.errors(SelectedComponent.props.model, SelectedComponent.props.form)}
                fieldNames={fieldNames}
              />
              {SelectedComponent}
            </Modal.Body>

            <Modal.Footer>
              {this.buttons(page, components, modelName)}
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    );
  }

  componentDidMount() {
    const {
      setRecipients,
    } = this.props;

    setRecipients();
  }

  buttons(page, components, modelName) {
    const {confirmation} = this.props;
    const SelectedComponent = components[page];

    const previousButton = <button className='btn btn-light' type='button' onClick={() => this.previousComponent()}>
      &lt; Previous
    </button>;
    const nextButton = <Control.button model={modelName} disabled={{pending: true}} className='btn btn-primary' type='submit'>
      Next &gt;
    </Control.button>;
    const submitButton = <Control.button model={modelName} disabled={{pending: true}} className='btn btn-primary' type='submit'>
      Submit
    </Control.button>;
    const doneButton = <button className='btn btn-light' type='button' onClick={() => this.close()}>
      Done
    </button>;

    return <div>
      {page > 0 && !(page === components.length - 1 && !!confirmation) && previousButton}

      {/* The following two buttons *cannot* be combined, otherwise, the "next" button will submit the form */}

      {(
        (page < components.length - 1 && !confirmation) ||
        (page < components.length - 2 && !!confirmation)
      ) && nextButton}

      {(
        (page === components.length - 1 && !confirmation) ||
        (page === components.length - 2 && !!confirmation)
      ) && !SelectedComponent.props.preventSubmit && submitButton}

      {(
        (page === components.length - 1 && !!confirmation)
      ) && doneButton}
    </div>;
  }

  errors(model, form) {
    const fieldNames = Object.keys(model || []);
    const errors = {};

    fieldNames.forEach((field) => {
      let stringFieldWithErrors = Array.isArray(form[field].errors);
      let objectFieldWithErrors = form[field].$form && typeof form[field].$form.errors === 'object';
      let fieldErrors = [];

      if (stringFieldWithErrors) {
        fieldErrors = form[field].errors;
      } else if (objectFieldWithErrors) {
        let errorsObject = form[field].$form.errors;

        Object.keys(errorsObject).forEach((i) => fieldErrors[i] = errorsObject[i]);
      }

      if (fieldErrors.length) {
        errors[field] = fieldErrors;
      }
    });

    return errors;
  }

  close() {
    const {reset} = this.props;

    reset();

    this.setState({
      showModal: false
    });

    ReactDOM.unmountComponentAtNode(ReactDOM.findDOMNode(this).parentNode);
  }

  components() {
    const {
      components,
      confirmation,
      form,
      getRelatedContacts,
      hideContactSelect,
      model,
      recipientFilter,
      schoolYearId,
      customer,
      communicationType,
      selectableContactTypes
    } = this.props;

    const ConfirmationComponent = confirmation ? [confirmation] : [];

    if (hideContactSelect){
      return components.concat(ConfirmationComponent);
    }

    return [
      <WizardSelectContacts
        schoolYearId={schoolYearId}
        model={model.contacts}
        form={form.contacts}
        formPrefix={`.contacts`}
        className='communication-workflow-modal-contact-select'
        customer={customer}
        communicationType={communicationType}
        selectableContactTypes={selectableContactTypes}
        onSubmit={(values) => getRelatedContacts(
          values,
          recipientFilter,
          schoolYearId,
          model.contacts.combine_family_contacts
        )}
      />
    ].concat(components).concat(ConfirmationComponent);
  }

  fieldNames(fields) {
    const fieldNames = {};

    fields && fields.forEach((field) => {
      if (field.hasOwnProperty('canonicalName')) {
        fieldNames[field.canonicalName] = field.displayName;
      }
    });
    fieldNames['body'] = 'Body';
    fieldNames['relation'] = 'Intended recipients';

    return fieldNames;
  }

  nextComponent() {
    const {page, setPage} = this.props;

    if (page + 1 < this.components().length) {
      setPage(page + 1);
    }
  }

  previousComponent() {
    const {page, setPage} = this.props;

    setPage(page - 1);
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    getRelatedContacts: (filter, ids, recipientFilter, schoolYearId, combineFamilyContacts) =>
      dispatch(ownProps.actions.getRelatedContacts(filter, ids, recipientFilter, schoolYearId, combineFamilyContacts)),
    reset: () => dispatch(ownProps.actions.reset()),
    setRecipients: () => dispatch(ownProps.actions.setRecipients(ownProps.ids)),
    setPage: (page) => dispatch(ownProps.actions.setPage(page))
  }
};

export default connect(() => ({}), mapDispatchToProps)(CommunicationWorkflow);
